import { AutocompleteOption, FormCheckbox, LwFormAutocomplete } from 'redesign';
import { useFormContext } from 'react-hook-form';
import { Box, FilterOptionsState } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import {
  BusinessUnitsFilterCompany,
  BusinessUnitsWithCompanyFormContext,
} from './filter-business-units-with-company.types';
import { getBusinessUnitWithCompanyOptions } from './get-business-units-with-company-options';
import { FilterBusinessUnitsSelect } from './filter-business-units-multi-select';
import { filterValidDepartments, getFilterOptions } from '../../utils/utils';

type Props = {
  companies: BusinessUnitsFilterCompany[];
};

export const FilterBusinessUnitsWithCompany = (props: Props) => {
  const { companies } = props;
  const { setValue, watch, control } = useFormContext<BusinessUnitsWithCompanyFormContext>();

  const {
    company: selectedCompany,
    branchOffices: selectedBranchOffices,
    businessUnitCheckboxValue: selectedBusinessUnitCheckbox,
    departments: selectedDepartments,
  } = watch();

  const options = useMemo(() => {
    return getBusinessUnitWithCompanyOptions(
      companies,
      selectedCompany as AutocompleteOption | undefined,
      selectedBranchOffices as AutocompleteOption[] | undefined
    );
  }, [companies, selectedCompany, selectedBranchOffices]);

  useEffect(() => {
    const filteredDepartments = filterValidDepartments({
      selectedDepartments,
      selectedBranchOffices,
      companies,
      selectedCompanyId: selectedCompany?.value,
    });

    if (filteredDepartments.length !== (selectedDepartments?.length || 0)) {
      setValue('departments', filteredDepartments);
    }
  }, [selectedBranchOffices, selectedDepartments, selectedCompany, companies, setValue]);

  const filterOptions = useCallback(
    (
      autocompleteOptions: AutocompleteOption[] | null,
      autocompleteState: FilterOptionsState<any>
    ) => {
      const res = getFilterOptions(autocompleteOptions, autocompleteState);
      return res;
    },
    []
  );

  if (!selectedBusinessUnitCheckbox) {
    return (
      <Box>
        <FormCheckbox
          name="businessUnitCheckboxValue"
          control={control}
          label="Bedrijfseenheid"
          isHeaderCheckbox={true}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box>
        <FormCheckbox
          name="businessUnitCheckboxValue"
          control={control}
          label="Bedrijfseenheid"
          isHeaderCheckbox={true}
        />
      </Box>
      <Box paddingLeft="30px">
        <LwFormAutocomplete
          name="company"
          isLoading={false}
          label="Bedrijf"
          getOptionLabel={(value) => value?.label ?? ''}
          isOptionEqualToValue={(option, value) => {
            return option?.value === value.value;
          }}
          getOptionKey={(option) => option.value}
          options={options.companies}
          filterOptions={(options, state) => filterOptions(options, state)}
          control={control}
          onChange={(_event, value) => {
            if (value) {
              setValue('company', value);
              setValue('branchOffices', null);
              setValue('departments', null);
            }
          }}
        />
        <FilterBusinessUnitsSelect
          formName="branchOffices"
          label="Vestigingen"
          options={options.branchOffices}
          disabled={!selectedCompany}
        />

        <FilterBusinessUnitsSelect
          formName="departments"
          label="Afdelingen"
          options={options.departments}
          disabled={!(selectedCompany && selectedBranchOffices)}
        />
      </Box>
    </Box>
  );
};
